import { type Dispatch, type FC, useCallback, useMemo } from "react";

import { type ApiServiceDescriptor, CloudflowEngine } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { type TransformMethod } from "@doitintl/models-hooks";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CircularProgress, Divider, FormControl, IconButton, MenuItem, Select, Stack } from "@mui/material";

import { usePaginatedQuery } from "../../../../Components/hooks/usePaginatedQuery";
import { type ProviderFilter, type ServiceFilter } from "./hooks/useActionSearchDialogStateMachine";

const emptyValueId = "~~empty~~";

export const ActionSearchServiceFilter: FC<{
  emptyLabel: string;
  provider: ProviderFilter;
  value: ServiceFilter | undefined;
  onChange: Dispatch<ServiceFilter | undefined>;
}> = ({ emptyLabel, provider, value, onChange }) => {
  const query = useMemo(
    () => getCollection(CloudflowEngine).doc("integrations").collection("apis").doc(provider.id).collection("services"),
    [provider.id]
  );

  const transformServices: TransformMethod<ApiServiceDescriptor, ServiceFilter & { serviceName: string }> = useCallback(
    ({ serviceName, latestVersion }, { id }) => ({
      serviceName,
      id,
      name: serviceName,
      version: latestVersion,
    }),
    []
  );

  const {
    items: services,
    isLoading: loadingServices,
    handlePrevPage: loadPrevPage,
    handleNextPage: loadNextPage,
    hasNextPage,
    hasPrevPage,
  } = usePaginatedQuery({
    query,
    options: {
      transform: transformServices,
      orderBy: ["serviceName"],
    },
  });
  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 200,
        backgroundColor: "general.backgroundDark",
        "& .MuiOutlinedInput-root": {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          "& fieldset": {
            borderLeft: "none",
          },
          "&.Mui-focused fieldset": {
            border: "2px solid",
            borderColor: "primary.main",
          },
          "&:hover fieldset": {
            border: "1px solid",
          },
        },
      }}
    >
      <Select
        value={value?.id ?? emptyValueId}
        onChange={({ target: { value } }) => {
          onChange(value === emptyValueId ? undefined : services?.find(({ id }) => id === value));
        }}
        displayEmpty
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 500,
            },
          },
        }}
        sx={{
          "& .MuiSelect-select": {
            height: 40,
          },
        }}
      >
        <MenuItem value={emptyValueId}>{emptyLabel}</MenuItem>
        {value && !services?.find(({ id }) => id === value?.id) && (
          <MenuItem key={value.id} value={value.id}>
            {value.name}
          </MenuItem>
        )}
        <Divider />
        {services?.map(({ id, name }) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
        {loadingServices && !services && (
          <Stack height={144} alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        )}
        {!loadingServices && (hasNextPage || hasPrevPage) && (
          <>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="flex-end" pr={2}>
              <IconButton onClick={loadPrevPage} disabled={!hasPrevPage} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={loadNextPage} disabled={!hasNextPage} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </>
        )}
      </Select>
    </FormControl>
  );
};
